import type { ReactNode } from 'react'
import { AppearancePage } from './pages/app-experience/AppearancePage'
import { ComingSoonPage } from './stubs/ComingSoonPage'
import type { SlotId, SubNavId, TopNavId } from './settings-tree'

interface SlotRendererArgs {
  platform: 'electron' | 'web'
  topNavId: TopNavId
  subNavId: SubNavId
}

type SlotRenderer = (args: SlotRendererArgs) => ReactNode

/**
 * Slot-id → renderer map. Anything not listed here falls back to the
 * {@link ComingSoonPage} stub in {@link renderSlot}.
 *
 * Adding a new page:
 * 1. Create the page under `pages/<top-nav>/<Name>Page.tsx`
 * 2. Register it here with the matching `${topNavId}/${subNavId}` key
 * 3. Ship i18n keys + (optionally) extend `settings-tree.ts` if the slot
 *    didn't exist yet
 *
 * The key type is `SlotId`, so the TypeScript compiler rejects typos
 * like `'app-experiance/appearance'` or impossible pairs.
 */
const SLOT_REGISTRY: Partial<Record<SlotId, SlotRenderer>> = {
  'app-experience/appearance': ({ platform }) => <AppearancePage platform={platform} />,
}

/**
 * Resolve a slot id to its rendered content. Returns the registered page
 * for slots that have real content, otherwise the {@link ComingSoonPage}
 * stub. Used by {@link PreferencesShell} as the single dispatch point
 * between navigation state and page components.
 */
export function renderSlot(args: SlotRendererArgs): ReactNode {
  const id: SlotId = `${args.topNavId}/${args.subNavId}`
  const renderer = SLOT_REGISTRY[id]
  if (renderer) return renderer(args)
  return <ComingSoonPage topNavId={args.topNavId} subNavId={args.subNavId} />
}
